/**
 * DAAM Leads — Public page rendered at /l/:slug.
 *
 * Mobile-first, RTL by default. Renders ONLY what the backend hands us
 * from GET /api/leads/public/:slug — a minimal, PII-free payload:
 *   { slug, business_name, logo_url, short_description,
 *     buttons: { whatsapp|call|order|website: { enabled, url } } }
 *
 * P0 explicitly excludes any tracking (no lead_events), no redirects
 * through /r/, and no consent banner. Clicks navigate directly to
 * wa.me / tel: / order / website URLs handed back by the API.
 */
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  MessageCircle, Phone, ShoppingBag, Globe2, Loader2,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A dedicated axios call with NO credentials/cookies/token — public
// endpoint must remain fully anonymous.
const publicApi = axios.create({ baseURL: API_BASE, withCredentials: false });

const BUTTON_MAP = [
  { key: "whatsapp", icon: MessageCircle, label: "تواصل عبر واتساب",
    accent: "bg-[#25D366] hover:bg-[#1EBE5D]" },
  { key: "call",     icon: Phone,         label: "اتصال مباشر",
    accent: "bg-neutral-900 hover:bg-neutral-800" },
  { key: "order",    icon: ShoppingBag,   label: "اطلب الآن",
    accent: "bg-neutral-900 hover:bg-neutral-800" },
  { key: "website",  icon: Globe2,        label: "زيارة الموقع",
    accent: "bg-neutral-900 hover:bg-neutral-800" },
];

export default function PublicLeadPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState({ status: "loading", data: null });

  // Attribution params from the visit URL. Backend re-normalises both
  // — the frontend passes them verbatim, but the server always
  // ground-truths (unknown → other, absent → direct, bad campaign → null).
  const source   = searchParams.get("s") || "";
  const campaign = searchParams.get("c") || "";

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", data: null });
    publicApi.get(`/leads/public/${encodeURIComponent(slug)}`)
      .then((r) => {
        if (cancelled) return;
        setState({ status: "ok", data: r.data });
        // Fire-and-forget page-view beacon. The endpoint returns 204 and
        // silently drops for unpublished / missing pages, so we don't
        // block navigation on it or surface errors.
        publicApi.post(`/leads/track/view/${encodeURIComponent(slug)}`, {
          source:   source   || null,
          campaign: campaign || null,
        }).catch(() => { /* ignore — telemetry never blocks UX */ });
      })
      .catch((e) => {
        if (cancelled) return;
        const s = e?.response?.status;
        // 404 → slug doesn't exist or draft. 410 → owner subscription
        // lapsed. From the visitor's perspective the surface is the
        // same "not available" copy — we NEVER reveal billing state.
        if (s === 404 || s === 410) {
          setState({ status: "missing", data: null });
          return;
        }
        setState({ status: "error", data: null });
      });
    return () => { cancelled = true; };
  }, [slug, source, campaign]);

  if (state.status === "loading") {
    return (
      <FullScreen>
        <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
      </FullScreen>
    );
  }
  if (state.status === "missing") {
    return (
      <FullScreen>
        <div dir="rtl"
             data-testid="lead-public-unavailable"
             className="text-center max-w-xs">
          <h1 className="text-lg font-semibold text-neutral-900">
            هذه الصفحة غير متاحة حاليًا
          </h1>
          <p className="mt-2 text-sm text-neutral-500 leading-6">
            تأكد من الرابط، أو حاول مجددًا لاحقًا.
          </p>
          <Link to="/"
                className="mt-6 inline-block text-sm text-neutral-700 underline underline-offset-4">
            العودة إلى DAAM
          </Link>
        </div>
      </FullScreen>
    );
  }
  if (state.status === "error") {
    return (
      <FullScreen>
        <div dir="rtl" className="text-center max-w-xs">
          <h1 className="text-lg font-semibold text-neutral-900">
            تعذّر تحميل الصفحة
          </h1>
          <p className="mt-2 text-sm text-neutral-500 leading-6">
            حاول مجددًا بعد قليل.
          </p>
        </div>
      </FullScreen>
    );
  }

  const p = state.data;
  const buttons = BUTTON_MAP.filter((b) => p.buttons?.[b.key]?.enabled);
  // Build the tracked click URL from the server-supplied internal path
  // + the visit's own source/campaign. The real destination lives on
  // the server; the browser only sees /r/{slug}/{action}.
  // We also append a one-shot `_c` nonce to each click so the URL is
  // unique per click — Chrome's memory cache otherwise replays the
  // previous redirect without re-hitting the server, which would
  // silently swallow repeat clicks.
  const backendOrigin = process.env.REACT_APP_BACKEND_URL || "";
  const buildClickUrl = (buttonPath) => {
    if (!buttonPath) return "#";
    const qs = new URLSearchParams();
    if (source)   qs.set("s", source);
    if (campaign) qs.set("c", campaign);
    qs.set("_c", `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`);
    return `${backendOrigin}${buttonPath}?${qs.toString()}`;
  };
  const rotateHref = (e) => {
    try {
      const a = e.currentTarget;
      const u = new URL(a.href, window.location.origin);
      u.searchParams.set("_c", `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`);
      a.href = u.toString();
    } catch { /* keep the baked-in href */ }
  };
  return (
    <div dir="rtl"
         data-testid="lead-public-page"
         className="min-h-screen bg-neutral-50 flex flex-col items-center py-8 sm:py-12 px-4">
      {/* Card — capped width for mobile, comfortable on desktop */}
      <div className="w-full max-w-md rounded-3xl overflow-hidden bg-white shadow-sm ring-1 ring-neutral-100">
        <div className="p-6 sm:p-8 text-center bg-gradient-to-b from-neutral-50 to-white">
          <div className="mx-auto h-24 w-24 rounded-2xl overflow-hidden bg-neutral-100 flex items-center justify-center ring-1 ring-neutral-200">
            {p.logo_url ? (
              <img src={p.logo_url}
                   alt=""
                   className="w-full h-full object-cover"
                   loading="lazy" />
            ) : (
              <span className="text-3xl font-semibold text-neutral-400">
                {(p.business_name || "؟").trim().charAt(0)}
              </span>
            )}
          </div>
          <h1 className="mt-5 text-xl sm:text-2xl font-semibold text-neutral-900"
              data-testid="lead-public-name">
            {p.business_name}
          </h1>
          {p.short_description && (
            <p className="mt-2 text-sm sm:text-base text-neutral-600 leading-6"
               data-testid="lead-public-desc">
              {p.short_description}
            </p>
          )}
        </div>

        <div className="px-4 sm:px-6 pb-7 space-y-2.5">
          {buttons.length === 0 && (
            <p className="text-center text-sm text-neutral-400">
              لا توجد أزرار مفعّلة حاليًا.
            </p>
          )}
          {buttons.map((b) => (
            <a key={b.key}
               data-testid={`lead-btn-${b.key}`}
               href={buildClickUrl(p.buttons[b.key].path)}
               rel="nofollow noopener noreferrer"
               onMouseDown={rotateHref}
               onTouchStart={rotateHref}
               onContextMenu={rotateHref}
               target={b.key === "website" || b.key === "order" ? "_blank" : undefined}
               className={`w-full rounded-2xl px-4 py-4 text-white text-base font-medium flex items-center justify-center gap-2 transition-colors ${b.accent}`}>
              <b.icon className="h-5 w-5" />
              <span>{b.label}</span>
            </a>
          ))}
        </div>
      </div>

      {/* Micro-footer — brand attribution only */}
      <div className="mt-8 text-[11px] text-neutral-400">
        Powered by <span className="font-semibold text-neutral-500">DAAM</span>
      </div>
    </div>
  );
}

function FullScreen({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-neutral-50">
      {children}
    </div>
  );
}
